import { useState } from 'react';

import { Page, PageHeader } from '../components/layout/AppShell';
import { Card, CardHeader } from '../components/ui/Card';
import { AsyncBoundary, EmptyState, SkeletonRows } from '../components/ui/AsyncBoundary';
import { Modal, useToast } from '../components/ui/Overlay';
import { Button } from '../components/ui/Button';
import { Field, Input } from '../components/ui/Field';
import { Badge } from '../components/ui/Badge';
import { StatTile } from '../components/charts/StatTile';
import { OrgSettingsModal } from '../components/domain/OrgModals';
import { useResource, useMutation } from '../hooks/useResource';
import { api } from '../lib/api';
import * as fmt from '../lib/format';

/**
 * The platform view — organisations and the device inventory behind them.
 * Only a platform administrator has the permissions any of this needs.
 *
 * Creating an organisation creates its first administrator in the same step:
 * a tenant nobody can sign in to is not a tenant, and creating them separately
 * is how orphaned organisations appear.
 */
export function Platform() {
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const orgs = useResource('/api/orgs');
  const devices = useResource('/api/devices', { limit: 500 });

  const spare = (devices.data?.devices || []).filter((device) => !device.vehicle_id).length;
  const unallocated = (devices.data?.devices || []).filter((device) => !device.org_id).length;

  return (
    <Page className="space-y-4">
      <PageHeader
        title="Platform"
        subtitle="Organisations and device inventory"
        actions={
          <Button variant="accent" size="sm" icon="plus" onClick={() => setCreating(true)}>
            New organisation
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile label="Organisations" value={orgs.data?.organizations?.length ?? '—'} icon="building" tone="brand" compact />
        <StatTile label="Devices" value={devices.data?.devices?.length ?? '—'} icon="chip" tone="brand" compact />
        <StatTile label="Spare" value={spare} icon="chip" tone="warning" compact />
        <StatTile label="Unallocated" value={unallocated} icon="chip" tone="accent" compact />
      </div>

      <Card className="overflow-hidden">
        <CardHeader title="Organisations" icon="building" />
        <AsyncBoundary
          state={orgs}
          skeleton={<SkeletonRows rows={4} />}
          isEmpty={(data) => !data.organizations.length}
          empty={<EmptyState icon="building" title="No organisations yet" />}
        >
          {(data) => (
            <ul className="divide-y divide-line">
              {data.organizations.map((org) => (
                <li key={org.id}>
                  <button
                    type="button"
                    onClick={() => setEditing(org)}
                    className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-surface-2"
                  >
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-medium text-ink">{org.name}</p>
                    <p className="truncate font-mono text-[12px] text-ink-3">{org.slug}</p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <Badge tone="neutral" size="sm" icon={null}>
                      {org.vehicle_count} vehicles
                    </Badge>
                    <Badge tone="neutral" size="sm" icon={null}>
                      {org.device_count} devices
                    </Badge>
                    <Badge tone="neutral" size="sm" icon={null}>
                      {org.user_count} people
                    </Badge>
                    <Badge tone={org.status === 'ACTIVE' ? 'good' : 'warning'} size="sm" icon={null}>
                      {String(org.status).toLowerCase()}
                    </Badge>
                    <span className="text-[12px] text-ink-3">since {fmt.date(org.created_at)}</span>
                  </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </AsyncBoundary>
      </Card>

      <CreateOrgModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          toast.success('Organisation created with its first administrator.');
          setCreating(false);
          orgs.refetch();
        }}
      />

      <OrgSettingsModal
        open={Boolean(editing)}
        organization={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          orgs.refetch();
        }}
      />
    </Page>
  );
}

function CreateOrgModal({ open, onClose, onCreated }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', adminName: '', adminEmail: '', adminPassword: '' });

  const create = useMutation(async () => {
    await api.post('/api/orgs', form);
  });

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const submit = async () => {
    try {
      await create.run();
      setForm({ name: '', adminName: '', adminEmail: '', adminPassword: '' });
      onCreated();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New organisation"
      description="An organisation is a tenant boundary — its data is invisible to every other organisation."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={create.pending}
            onClick={submit}
            disabled={!form.name || !form.adminEmail || form.adminPassword.length < 10}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Organisation name" required>
          <Input value={form.name} onChange={set('name')} placeholder="Northwind Logistics" />
        </Field>
        <div className="border-t border-line pt-3">
          <p className="mb-3 text-[12.5px] text-ink-3">
            Its first administrator, created in the same step.
          </p>
          <div className="space-y-3">
            <Field label="Administrator name" required>
              <Input value={form.adminName} onChange={set('adminName')} />
            </Field>
            <Field label="Administrator email" required>
              <Input type="email" value={form.adminEmail} onChange={set('adminEmail')} />
            </Field>
            <Field
              label="Temporary password"
              required
              error={
                form.adminPassword && form.adminPassword.length < 10
                  ? 'Use at least 10 characters.'
                  : null
              }
            >
              <Input type="text" value={form.adminPassword} onChange={set('adminPassword')} />
            </Field>
          </div>
        </div>
      </div>
    </Modal>
  );
}
